/**
 * @module utils/timestamp
 * @description Timestamp formatting utilities for consistent date/time output.
 * Provides a standardized timestamp format: YYYY-MM-DD HH:mm:ss,SSS
 * This module has no dependencies on other modules.
 */

/**
 * Pads a number with leading zero to ensure two-digit format
 * @function addZero
 * @param {number} num - The number to pad
 * @returns {string} Two-digit string representation
 * @private
 *
 * @example
 * addZero(5) // '05'
 * addZero(12) // '12'
 */
function addZero(num: number) {
    return `${num}`.padStart(2, '0');
}

/**
 * Pads a number with leading zeros to ensure three-digit format
 * @function addTwoZero
 * @param {number} num - The number to pad (typically milliseconds)
 * @returns {string} Three-digit string representation
 * @private
 *
 * @example
 * addTwoZero(5) // '005'
 * addTwoZero(84) // '084'
 * addTwoZero(844) // '844'
 */
function addTwoZero(num: number) {
    return `${num}`.padStart(3, '0');
}

/**
 * Formats the current timestamp in a standardized format
 * @function printTimestamp
 * @returns {string} Formatted timestamp string in the format: YYYY-MM-DD HH:mm:ss,SSS
 * @description Generates a timestamp string with year, month, day, hours, minutes,
 * seconds, and milliseconds. Useful for logging and debugging.
 *
 * @example
 * printTimestamp() // '2024-04-07 12:22:21,844'
 * printTimestamp() // '2025-11-10 09:15:03,125'
 */
function printTimestamp() {
    const time = new Date();

    return (
        time.getFullYear() +
        '-' +
        addZero(time.getMonth() + 1) +
        '-' +
        addZero(time.getDate()) +
        ' ' +
        addZero(time.getHours()) +
        ':' +
        addZero(time.getMinutes()) +
        ':' +
        addZero(time.getSeconds()) +
        ',' +
        addTwoZero(time.getMilliseconds())
    );
}

export default printTimestamp;
